import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { LocalizationContext } from '../contexts/LocalizationContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * In-memory cache of `pageSlug → blocks` responses. A single fetch fills the
 * entire page's editable blocks, so every subsequent `useContentBlock` call
 * for the same page is instant.
 */
const cache = new Map();
const inflight = new Map();

async function fetchBlocks(pageSlug, country) {
  const key = `${pageSlug}:${country}`;
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);

  const promise = axios
    .get(`${API_BASE}/api/pages/blocks/${pageSlug}`, { params: { country } })
    .then((res) => {
      const blocks = res.data?.blocks || {};
      // Rewrite any legacy /api/media/serve/ image URLs to CDN.
      Object.values(blocks).forEach((b) => {
        if (Array.isArray(b.images)) {
          b.images = b.images.map((img) => ({
            ...img,
            url: resolveMediaUrl(img.url),
            thumbnail: img.thumbnail ? resolveMediaUrl(img.thumbnail) : img.thumbnail,
          }));
        }
      });
      cache.set(key, blocks);
      inflight.delete(key);
      return blocks;
    })
    .catch(() => {
      // Page or blocks endpoint 404 → treat as empty. Callers fall back to defaults.
      cache.set(key, {});
      inflight.delete(key);
      return {};
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Bust the cache after an admin save so open storefront tabs pull fresh blocks
 * on the next mount. Intended for use in AdminPageForm after PUT succeeds.
 */
export function invalidateContentBlocks(pageSlug) {
  for (const k of Array.from(cache.keys())) {
    if (!pageSlug || k.startsWith(`${pageSlug}:`)) cache.delete(k);
  }
}

/**
 * React hook: fetches a single content block by key from the admin-managed
 * page sections. Returns the block if present, else `null` so callers can
 * render their hardcoded defaults.
 *
 *   const block = useContentBlock('home-editorial', 'home.manifest.body');
 *   const body = block?.body || DEFAULT_BODY;
 *
 * The `country` defaults to the storefront's selected country (falling back to
 * 'CO'), so each country gets its own language version of the blocks. Callers
 * that need a different country should pass it explicitly.
 */
export function useContentBlock(pageSlug, key, { country: countryOverride } = {}) {
  const localization = useContext(LocalizationContext);
  const country = countryOverride || localization?.countryCode || 'CO';
  const [block, setBlock] = useState(() => {
    const cached = cache.get(`${pageSlug}:${country}`);
    return cached ? cached[key] || null : null;
  });

  useEffect(() => {
    let alive = true;
    fetchBlocks(pageSlug, country).then((blocks) => {
      if (alive) setBlock(blocks[key] || null);
    });
    return () => {
      alive = false;
    };
  }, [pageSlug, key, country]);

  return block;
}

/**
 * Fetch all blocks for a page as a `key → block` map. Handy for pages that
 * render many editable sections in the same tree (e.g. AboutPage).
 */
export function useContentBlocks(pageSlug, { country: countryOverride } = {}) {
  const localization = useContext(LocalizationContext);
  const country = countryOverride || localization?.countryCode || 'CO';
  const [blocks, setBlocks] = useState(() => cache.get(`${pageSlug}:${country}`) || {});

  useEffect(() => {
    let alive = true;
    fetchBlocks(pageSlug, country).then((b) => {
      if (alive) setBlocks(b);
    });
    return () => {
      alive = false;
    };
  }, [pageSlug, country]);

  return blocks;
}
